import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import {
  InputGroup,
  InputLeftElement,
  Input,
  Divider,
  VStack,
  Box,
  Checkbox,
  Wrap,
  WrapItem,
  Tag,
  HStack,
  Text,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { CustomPopover } from "../custom-popover";
import { useFirebaseUser } from "utils/useFirebaseUser";

interface Props {
  value: string[];
  onChange: (newValue: string[]) => void;
}

export function CompanyTagsFilter({ value, onChange }: Props) {
  const [companyTags, setCompanyTags] = useState<string[]>([]);
  const [filteredCompanyTags, setFilteredCompanyTags] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const { data: session } = useSession() ?? {};
  const isAuthenticated = useFirebaseUser();

  useEffect(() => {
    if (session && isAuthenticated && !hasFetched) {
      setHasFetched(true);
      fetchCompanyTags();
    }
  }, [session, isAuthenticated]);

  const fetchCompanyTags = async () => {
    try {
      const response = await fetch("/api/database/getCompanyTags");
      const data = await response.json();

      if (data.success) {
        setCompanyTags(data.companyTags);
        setFilteredCompanyTags(data.companyTags);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!companyTags || companyTags.length === 0) {
      return;
    }

    if (!searchValue) {
      setFilteredCompanyTags([...companyTags]);
    } else {
      setFilteredCompanyTags(
        companyTags.filter((i) =>
          i.toLowerCase().includes(searchValue.toLowerCase())
        )
      );
    }
  }, [searchValue]);

  return (
    <CustomPopover
      property="Company Tags"
      value={value?.length === 0 ? "all" : value.length === 1 ? value[0] : value.length + " selected"}
      popoverBodyProps={{ p: 0, fontSize: "sm" }}
      popoverContentProps={{ borderRadius: "xl" }}
      isActive={value?.length > 0}
    >
      <InputGroup size="sm" mt="1">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.300" />
        </InputLeftElement>
        <Input
          border="none"
          placeholder="Search company tags"
          _active={{ boxShadow: "none" }}
          _focus={{ boxShadow: "none" }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const newValues = [...value]
              if (!value.includes(searchValue)) {
                newValues.push(searchValue)
              }
              setSearchValue("");
              onChange(newValues);
            }
          }}
        />
      </InputGroup>
      <Divider mt="1" mb="2" />
      <VStack
        overflowY="auto"
        maxH="80"
        className="noScrollBar"
        alignItems="flex-start"
        px="4"
        pb="2"
      >
        {filteredCompanyTags.map((i) => (
          <Box>
            <Checkbox
              isChecked={value.includes(i)}
              onChange={(e) => {
                let newValues = [...value];
                if (e.target.checked) {
                  newValues.push(i);
                } else {
                  newValues = newValues.filter((v) => v !== i);
                }

                onChange(newValues);
              }}
            >
              {i}
            </Checkbox>
          </Box>
        ))}
      </VStack>
      <Divider />
      {value?.length > 0 ? (
        <Wrap p="3" spacing="2">
          {value.map((v) => (
            <WrapItem>
              <Tag size="sm" color="white" bg="#31C52E">
                <HStack>
                  <Text>{v}</Text>
                  <CloseIcon
                    h="2"
                    w="2"
                    cursor="pointer"
                    onClick={() => {
                      onChange([...value].filter((r) => r !== v));
                    }}
                  />
                </HStack>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      ) : (
        <></>
      )}
    </CustomPopover>
  );
}
